import { z } from "zod";

// ===============================================================
// CATALÓGO DE PAQUETES  (ssot, compartido por backend + admin UI)
// ===============================================================

// Justificación de precios:
//   Costo de la API de tokens de sesión de Google Places  ≈ $0.02 / sesión
//   Nuestro precio                                        = $0.025 / sesión (con margen)
//   Los paquetes usan cantidades de créditos redondeadas para una UX clara.

export const AUTOCOMPLETE_PACKAGES = [
  { id: "starter", name: "Starter Pack", price_usd: 5, credits: 200 },
  { id: "basic", name: "Basic Pack", price_usd: 10, credits: 400 },
  { id: "growth", name: "Growth Pack", price_usd: 20, credits: 800 },
  { id: "pro", name: "Pro Pack", price_usd: 50, credits: 2000 },
] as const;

export type AutocompletePackageId =
  (typeof AUTOCOMPLETE_PACKAGES)[number]["id"];

export const AutocompletePackageIdSchema = z.enum(
  AUTOCOMPLETE_PACKAGES.map((p) => p.id) as [
    AutocompletePackageId,
    ...AutocompletePackageId[],
  ]
);

// =========================================================================
// PUT /admin/autocomplete/config
// =========================================================================

export const UpdateAutocompleteConfigRequestSchema = z.object({
  enabled: z.boolean(),
});

export const AutocompleteConfigResponseSchema = z.object({
  enabled: z.boolean(),
  credits: z.number().int().nonnegative(),
  sessions_used: z.number().int().nonnegative(),
});

// =========================================================================
// GET /admin/autocomplete/balance
// =========================================================================

export const AutocompleteBalanceResponseSchema = z.object({
  credits: z.number().int().nonnegative(),
  sessions_used: z.number().int().nonnegative(),
  enabled: z.boolean(),
  // Recent purchases for the admin UI history table
  recent_purchases: z.array(
    z.object({
      id: z.string(),
      package_id: AutocompletePackageIdSchema,
      price_usd: z.number(),
      credits_granted: z.number().int(),
      status: z.enum(["PENDING", "CONFIRMED", "DECLINED", "EXPIRED"]),
      created_at: z.iso.datetime(),
      confirmed_at: z.iso.datetime().nullable(),
    })
  ),
  // Convenience list so the admin UI can render "Buy credits" buttons
  packages: z.array(
    z.object({
      id: AutocompletePackageIdSchema,
      name: z.string(),
      price_usd: z.number(),
      credits: z.number().int(),
      price_per_credit: z.number(), // computed, for display
    })
  ),
});

// =========================================================================
// POST /admin/autocomplete/topup  (initiate Shopify one-time charge)
// =========================================================================

export const TopupRequestSchema = z.object({
  package_id: AutocompletePackageIdSchema,
  // Where Shopify should redirect after the merchant approves/declines.
  return_url: z.string().url(),
});

export const TopupResponseSchema = z.object({
  purchase_id: z.string(), // Our internal AutocompletePurchase.id
  confirmation_url: z.string().url(), // Redirect merchant here
});

// =========================================================================
// GET /autocomplete/suggestions  (widget → backend, proxies Google Autocomplete)
// =========================================================================

export const AutocompleteQueryRequestSchema = z.object({
  shop: z.string().min(1),
  input: z.string().min(2).max(100),
  // Generado por el frontend, agrupa todos los keystrokes de una búsqueda.
  // El backend lo usa como key en redis para reutilizar el session_token de Google.
  session_id: z.string().uuid(),
});

export const AutocompleteQueryResponseSchema = z.discriminatedUnion(
  "authorized",
  [
    z.object({
      authorized: z.literal(true),
      predictions: z.array(
        z.object({
          place_id: z.string(),
          description: z.string(),
          main_text: z.string(),
          secondary_text: z.string(),
        })
      ),
      credits_remaining: z.number().int().nonnegative(),
    }),
    z.object({
      authorized: z.literal(false),
      reason: z.enum(["insufficient_credits", "disabled"]),
    }),
  ]
);

// =========================================================================
// POST /autocomplete/place-details  (widget → backend, closes Google session)
// =========================================================================

export const PlaceDetailsRequestSchema = z.object({
  place_id: z.string().min(1),
  session_id: z.string().uuid(),
});

export const PlaceDetailsResponseSchema = z.object({
  place_id: z.string(),
  formatted_address: z.string(),
  // Raw components kept for any custom parsing the widget might need.
  address_components: z.array(
    z.object({
      long_name: z.string(),
      short_name: z.string(),
      types: z.array(z.string()),
    })
  ),
  geometry: z
    .object({
      location: z.object({ lat: z.number(), lng: z.number() }),
    })
    .optional(),
  // Parsed fields ready to fill checkout form inputs directly.
  // Empty string when the component is absent in the Google response.
  city: z.string(),
  zone: z.string(),
  state: z.string(),
  country: z.string(),
  postal_code: z.string(),
});

// =========================================================================
// TYPE EXPORTS
// =========================================================================

export type UpdateAutocompleteConfigRequest = z.infer<
  typeof UpdateAutocompleteConfigRequestSchema
>;
export type AutocompleteConfigResponse = z.infer<
  typeof AutocompleteConfigResponseSchema
>;
export type AutocompleteBalanceResponse = z.infer<
  typeof AutocompleteBalanceResponseSchema
>;
export type TopupRequest = z.infer<typeof TopupRequestSchema>;
export type TopupResponse = z.infer<typeof TopupResponseSchema>;
export type AutocompleteQueryRequest = z.infer<
  typeof AutocompleteQueryRequestSchema
>;
export type AutocompleteQueryResponse = z.infer<
  typeof AutocompleteQueryResponseSchema
>;
export type PlaceDetailsRequest = z.infer<typeof PlaceDetailsRequestSchema>;
export type PlaceDetailsResponse = z.infer<typeof PlaceDetailsResponseSchema>;
